import { describe, it } from 'node:test'
import assert from 'node:assert/strict'
import { transformContent } from '../assets.js'

const TEST_HOME = '/Users/test/.claude/gsd-munch'

describe('transformContent', () => {
  describe('Pattern 1: bash-invoked gsd-tools path', () => {
    it('replaces "$HOME/.claude/get-shit-done/bin/gsd-tools.cjs" with absolute gsd-munch path', () => {
      const input = 'node "$HOME/.claude/get-shit-done/bin/gsd-tools.cjs" init'
      const output = transformContent(input, TEST_HOME)
      assert.equal(output, `node "${TEST_HOME}/bin/gsd-tools.cjs" init`)
    })

    it('replaces all occurrences of the bash-invoked path', () => {
      const input = [
        'node "$HOME/.claude/get-shit-done/bin/gsd-tools.cjs" init',
        'node "$HOME/.claude/get-shit-done/bin/gsd-tools.cjs" state load',
      ].join('\n')
      const output = transformContent(input, TEST_HOME)
      assert.ok(!output.includes('get-shit-done'), 'Output must not contain get-shit-done')
      assert.equal(output.split(`${TEST_HOME}/bin/gsd-tools.cjs`).length - 1, 2)
    })
  })

  describe('Pattern 2: .claude/get-shit-done/ path fragments', () => {
    it('replaces @-reference absolute paths with gsd-munch home path', () => {
      const input = '@/Users/danieltopham/.claude/get-shit-done/workflows/plan-phase.md'
      const output = transformContent(input, TEST_HOME)
      assert.equal(output, `@${TEST_HOME}/workflows/plan-phase.md`)
    })

    it('replaces get-shit-done path fragments in general absolute paths', () => {
      const input = '/Users/danieltopham/.claude/get-shit-done/references/checkpoints.md'
      const output = transformContent(input, TEST_HOME)
      assert.equal(output, `${TEST_HOME}/references/checkpoints.md`)
    })

    it('replaces multiple @-references in the same content', () => {
      const input = [
        '@/Users/danieltopham/.claude/get-shit-done/workflows/plan-phase.md',
        '@/Users/danieltopham/.claude/get-shit-done/references/ui-brand.md',
      ].join('\n')
      const output = transformContent(input, TEST_HOME)
      assert.ok(!output.includes('get-shit-done'), 'Output must not contain get-shit-done')
    })
  })

  describe('Pattern 3: /gsd: command namespace', () => {
    it('replaces /gsd: command references with /gm:', () => {
      const input = 'run /gsd:research-phase before /gsd:plan-phase'
      const output = transformContent(input, TEST_HOME)
      assert.equal(output, 'run /gm:research-phase before /gm:plan-phase')
    })

    it('replaces all /gsd: occurrences', () => {
      const input = 'Use /gsd:new-project, /gsd:plan-phase, /gsd:execute-phase'
      const output = transformContent(input, TEST_HOME)
      assert.equal(output, 'Use /gm:new-project, /gm:plan-phase, /gm:execute-phase')
      assert.ok(!output.includes('/gsd:'), 'Output must not contain /gsd:')
    })
  })

  describe('Pattern 4: name: gsd: frontmatter field', () => {
    it('replaces "name: gsd:" frontmatter with "name: gm:"', () => {
      const input = 'name: gsd:plan-phase'
      const output = transformContent(input, TEST_HOME)
      assert.equal(output, 'name: gm:plan-phase')
    })

    it('replaces name: gsd: in multiline frontmatter', () => {
      const input = '---\nname: gsd:plan-phase\ndescription: Create phase plans\n---'
      const output = transformContent(input, TEST_HOME)
      assert.ok(output.includes('name: gm:plan-phase'), 'name field should use gm: prefix')
      assert.ok(!output.includes('name: gsd:'), 'Output must not contain name: gsd:')
    })
  })

  describe('no-op cases', () => {
    it('returns content unchanged when no patterns match', () => {
      const input = 'Hello, world! This content has no matching patterns.'
      const output = transformContent(input, TEST_HOME)
      assert.equal(output, input)
    })

    it('handles empty string', () => {
      const output = transformContent('', TEST_HOME)
      assert.equal(output, '')
    })
  })

  describe('combined patterns', () => {
    it('applies all replacements when all patterns are present', () => {
      const input = [
        'node "$HOME/.claude/get-shit-done/bin/gsd-tools.cjs" init',
        '@/Users/danieltopham/.claude/get-shit-done/workflows/plan-phase.md',
        'run /gsd:research-phase',
        'name: gsd:plan-phase',
      ].join('\n')
      const output = transformContent(input, TEST_HOME)
      assert.ok(!output.includes('get-shit-done'), 'Output must not contain get-shit-done after transformation')
      assert.ok(!output.includes('/gsd:'), 'Output must not contain /gsd:')
      assert.ok(output.includes(`"${TEST_HOME}/bin/gsd-tools.cjs"`), 'Should have absolute gsd-tools path')
      assert.ok(output.includes(`@${TEST_HOME}/workflows/plan-phase.md`), 'Should have transformed @-ref')
      assert.ok(output.includes('/gm:research-phase'), 'Should have /gm: namespace')
      assert.ok(output.includes('name: gm:plan-phase'), 'Should have gm: name field')
    })

    it('critical: no deployed output contains the string get-shit-done', () => {
      const sampleWorkflowContent = `---
name: gsd:execute-phase
description: Execute a phase plan
---
<objective>
Execute the plan.
</objective>

<execution_context>
@/Users/danieltopham/.claude/get-shit-done/workflows/execute-plan.md
</execution_context>

\`\`\`bash
INIT=$(node "$HOME/.claude/get-shit-done/bin/gsd-tools.cjs" init execute-phase)
\`\`\`

After done, run /gsd:verify-work.
`
      const output = transformContent(sampleWorkflowContent, TEST_HOME)
      assert.ok(
        !output.includes('get-shit-done'),
        `Output must not contain "get-shit-done". Found in: ${output}`
      )
    })
  })
})
